import { projectPosition } from './flightProjection.js';
import { metersBetween } from './geo.js';
import { isResumeFrame } from './pageResume.js';
import {
  FLIGHT_EASE_MS,
  FLIGHT_MAX_EXTRAP_MS,
  FLIGHT_HOVER_GS_KT,
  FLIGHT_SNAP_METERS,
} from './flightMotion.js';

/** One reported aircraft for this frame. */
export interface FlightFix {
  id: string;
  position: [number, number];
  headingDeg: number | null;
  gsKt: number | null;
}

/** What to draw for one aircraft this frame. */
export interface FlightSample {
  position: [number, number];
  headingDeg: number;
}

export interface FlightTracker {
  /** Advance every aircraft to this frame; entities absent from `fixes` are pruned.
   * `generatedAt` detects a fresh poll so corrections only restart on new data. */
  step(fixes: FlightFix[], generatedAt: number, nowMs: number): Map<string, FlightSample>;
}

interface Tracked {
  lon: number;
  lat: number;
  headingDeg: number;
  gsKt: number;
  fixTime: number;
  /** Display − target offset captured at the last fix, eased to zero over EASE_MS. */
  offset: [number, number];
  offsetStart: number;
}

/** Effective ground speed for projection: hovering aircraft don't translate. */
function effectiveGs(gsKt: number): number {
  return gsKt < FLIGHT_HOVER_GS_KT ? 0 : gsKt;
}

/** Target (un-eased) position for a tracked aircraft at `nowMs`. */
function target(t: Tracked, nowMs: number): [number, number] {
  const dt = Math.min(Math.max(nowMs - t.fixTime, 0), FLIGHT_MAX_EXTRAP_MS);
  return projectPosition(t.lon, t.lat, t.headingDeg, effectiveGs(t.gsKt), dt);
}

/** Eased display position: target plus the decaying correction offset. */
function display(t: Tracked, nowMs: number): [number, number] {
  const [tx, ty] = target(t, nowMs);
  const k = 1 - Math.min(Math.max((nowMs - t.offsetStart) / FLIGHT_EASE_MS, 0), 1);
  return [tx + t.offset[0] * k, ty + t.offset[1] * k];
}

export function createFlightTracker(): FlightTracker {
  let tracked = new Map<string, Tracked>();
  let lastGeneratedAt = 0;

  return {
    step(fixes, generatedAt, nowMs) {
      const isFreshPoll = generatedAt !== lastGeneratedAt;
      if (isFreshPoll) lastGeneratedAt = generatedAt;

      // First frame back from a hidden tab: render exactly at the report with no
      // correction offset, so aircraft don't ease across the gap that piled up while
      // rAF was suspended. (A long enough background already trips `isStale` below;
      // this also covers shorter backgrounds within the extrapolation window.)
      const resumed = isResumeFrame();

      const next = new Map<string, Tracked>();
      const samples = new Map<string, FlightSample>();

      for (const f of fixes) {
        const prev = tracked.get(f.id);
        const heading = f.headingDeg ?? prev?.headingDeg ?? 0;
        const gsKt = f.gsKt ?? prev?.gsKt ?? 0;

        const isStale = !!prev && (nowMs - prev.fixTime > FLIGHT_MAX_EXTRAP_MS);

        let entity: Tracked;
        if (!prev || isStale || resumed) {
          // First sight or stale fix: render exactly at the report, no correction.
          entity = {
            lon: f.position[0], lat: f.position[1], headingDeg: heading, gsKt,
            fixTime: nowMs, offset: [0, 0], offsetStart: nowMs,
          };
        } else if (isFreshPoll) {
          // Capture where the marker is shown right now, re-anchor to the new fix,
          // and store the (shown − newTarget) offset so the marker eases over, not jumps.
          const shown = display(prev, nowMs);
          const candidate: Tracked = {
            lon: f.position[0], lat: f.position[1], headingDeg: heading, gsKt,
            fixTime: nowMs, offset: [0, 0], offsetStart: nowMs,
          };
          const newTarget = target(candidate, nowMs); // == f.position at dt 0
          const off: [number, number] = [shown[0] - newTarget[0], shown[1] - newTarget[1]];
          // Teleport rather than ease across a large data-gap correction.
          candidate.offset = metersBetween(shown, newTarget) > FLIGHT_SNAP_METERS ? [0, 0] : off;
          entity = candidate;
        } else {
          // Same poll, just animating — keep the fix, advance extrapolation + ease.
          entity = prev;
        }

        next.set(f.id, entity);
        samples.set(f.id, { position: display(entity, nowMs), headingDeg: entity.headingDeg });
      }

      tracked = next; // prunes aircraft no longer in the feed
      return samples;
    },
  };
}
